import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

// Επιλογές γλώσσας. Το 'gr' είναι ο εσωτερικός κωδικός· προς τον χρήστη δείχνουμε 'EL'.
const LANG_OPTIONS = [
  { code: 'en', short: 'EN', label: 'English' },
  { code: 'gr', short: 'EL', label: 'Ελληνικά' },
  { code: 'de', short: 'DE', label: 'Deutsch' },
  { code: 'it', short: 'IT', label: 'Italiano' },
  { code: 'fr', short: 'FR', label: 'Français' },
];

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/villa', label: t('nav.villa') },
    { path: '/bedrooms', label: t('nav.bedrooms') },
    { path: '/amenities', label: t('nav.amenities') },
    { path: '/gallery', label: t('nav.gallery') },
    { path: '/reviews', label: t('nav.reviews') },
    { path: '/location', label: t('nav.location') },
    { path: '/contact', label: t('nav.contact') },
  ];

  const isHomePage = location.pathname === '/';
  const navBackground = isScrolled || !isHomePage
    ? 'bg-white/95 backdrop-blur-md shadow-sm'
    : 'bg-transparent';
  const textColor = isScrolled || !isHomePage ? 'text-[#1E3A5F]' : 'text-white';
  const logoGold = isScrolled || !isHomePage ? 'text-[#D4A72C]' : 'text-[#D4A72C]';

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBackground}`}
    >
      {/* Gold accent line */}
      {(isScrolled || !isHomePage) && <div className="h-0.5 bg-[#D4A72C]" />}
      
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="nav-logo"
            className={`font-serif text-xl md:text-2xl font-semibold transition-colors ${textColor}`}
          >
            Villa <span className={logoGold}>Aristotle</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.slice(0, 6).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                className={`nav-link ${textColor} hover:text-[#D4A72C] ${
                  location.pathname === link.path ? 'text-[#D4A72C]' : 'opacity-80'
                }`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* More dropdown for additional links */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-more-dropdown"
                  className={`nav-link ${textColor} opacity-80 hover:text-[#D4A72C]`}
                >
                  {t('nav.more')}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                {navLinks.slice(6).map((link) => (
                  <DropdownMenuItem key={link.path} asChild>
                    <Link
                      to={link.path}
                      data-testid={`nav-dropdown-${link.path.replace('/', '')}`}
                      className="w-full"
                    >
                      {link.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem asChild>
                  <Link to="/policies" data-testid="nav-dropdown-policies">
                    {t('nav.policies')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/host" data-testid="nav-dropdown-host">
                    {t('nav.host')}
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Right side - Language & CTA */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="language-toggle"
                  aria-label="Language"
                  className={`flex items-center gap-1.5 nav-link ${textColor} opacity-80 hover:text-[#D4A72C]`}
                >
                  <Globe className="w-4 h-4" />
                  <span>{LANG_OPTIONS.find((l) => l.code === language)?.short}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {LANG_OPTIONS.map((opt) => (
                  <DropdownMenuItem
                    key={opt.code}
                    data-testid={`language-option-${opt.code}`}
                    onClick={() => setLanguage(opt.code)}
                    className={language === opt.code ? 'text-[#D4A72C] font-semibold' : ''}
                  >
                    {opt.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Book Now CTA */}
            <Link to="/booking">
              <Button
                data-testid="nav-book-now-btn"
                className="bg-[#D4A72C] hover:bg-[#D4A72C]/90 text-[#1E3A5F] font-semibold btn-lift"
              >
                {t('nav.booking')}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 ${textColor}`}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white/98 backdrop-blur-md border-t border-[#D4A72C]/20 animate-fade-in"
        >
          <div className="max-w-7xl mx-auto px-6 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.path.replace('/', '') || 'home'}`}
                className={`block py-2 text-[#1E3A5F] nav-link ${
                  location.pathname === link.path ? 'text-[#D4A72C]' : ''
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/policies"
              data-testid="mobile-nav-policies"
              className="block py-2 text-[#1E3A5F] nav-link"
            >
              {t('nav.policies')}
            </Link>
            <Link
              to="/host"
              data-testid="mobile-nav-host"
              className="block py-2 text-[#1E3A5F] nav-link"
            >
              {t('nav.host')}
            </Link>
            
            <div className="pt-4 border-t border-[#1E3A5F]/10 flex items-center justify-between">
              <div
                data-testid="mobile-language-toggle"
                className="flex items-center gap-2 text-[#1E3A5F]/60"
              >
                <Globe className="w-4 h-4" />
                {LANG_OPTIONS.map((opt) => (
                  <button
                    key={opt.code}
                    data-testid={`mobile-language-option-${opt.code}`}
                    onClick={() => setLanguage(opt.code)}
                    className={`text-sm px-1 ${language === opt.code ? 'text-[#D4A72C] font-semibold' : ''}`}
                  >
                    {opt.short}
                  </button>
                ))}
              </div>
              
              <Link to="/booking">
                <Button
                  data-testid="mobile-book-now-btn"
                  className="bg-[#D4A72C] hover:bg-[#D4A72C]/90 text-[#1E3A5F] font-semibold"
                >
                  {t('nav.booking')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
